/* THE UPLOAD QUEUE (W82 step 5) — ONE ordered queue that runs at Send, after the undo window, and a tiny store the
 * containers read so each says `Uploading…` and then `▶ 0:41`. The storage calls are INJECTED, so `mediaUpload.test.ts`
 * drives the whole order — upload each, stop at the first failure, remove what landed — with no network.
 *
 * ⚠️ WHY IT OUTLIVES THE PAGE: the send fires ten seconds after the click, maybe after the coach has left the review; the
 * statuses therefore live here, keyed by the object's path, not in the page's state. */
import type { UploadItem, UploadStatus } from "./reviewMedia";

type Listener = () => void;
const statuses = new Map<string, UploadStatus>();
const listeners = new Set<Listener>();
let version = 0;

export function subscribeUploads(l: Listener): () => void {
  listeners.add(l);
  return () => { listeners.delete(l); };
}
/** A number that changes whenever any status changes (for `useSyncExternalStore`). */
export function uploadsVersion(): number {
  return version;
}
export function uploadStatus(path: string): UploadStatus | null {
  return statuses.get(path) ?? null;
}
function set(path: string, s: UploadStatus | null) {
  if (s === null) statuses.delete(path); else statuses.set(path, s);
  version += 1;
  for (const l of listeners) l();
}

export type Upload = (path: string, blob: Blob, contentType: string) => Promise<string | null>;
export type Remove = (paths: string[]) => Promise<string | null>;

/**
 * Upload every item IN ORDER (one at a time — a coach's connection is not a CDN, and the containers fill one by one in the
 * order they are drawn). The first failure stops the queue, removes everything that already landed (the owner's delete),
 * and is returned; nothing is left half-sent. On success every item reads `done` until `forget`.
 */
export async function runUploads(
  items: readonly UploadItem[], blobOf: (ref: string) => Blob | undefined, upload: Upload, remove: Remove,
): Promise<{ error: string | null; landed: string[] }> {
  const landed: string[] = [];
  for (const it of items) set(it.path, "uploading");
  for (const it of items) {
    const blob = blobOf(it.ref);
    const err = blob === undefined ? "the recording is no longer in this tab" : await upload(it.path, blob, it.contentType);
    if (err !== null) {
      set(it.path, "failed");
      for (const rest of items) if (rest !== it && uploadStatus(rest.path) === "uploading") set(rest.path, null);
      if (landed.length > 0) await remove(landed);
      for (const p of landed) set(p, null);
      return { error: err, landed: [] };
    }
    landed.push(it.path);
    set(it.path, "done");
  }
  return { error: null, landed };
}

/** The rows failed to insert after the objects landed: remove them again, so no object outlives a row that never was. */
export async function undoUploads(paths: readonly string[], remove: Remove): Promise<string | null> {
  if (paths.length === 0) return null;
  const err = await remove([...paths]);
  for (const p of paths) set(p, err === null ? null : "failed");
  return err;
}

export function forgetUploads(paths: readonly string[]): void {
  for (const p of paths) set(p, null);
}
